import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useRef } from 'react';
import { Settings, X, Upload, Check, Trash2, Palette, Image as ImageIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription } from
'@/shared/components/ui/sheet';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Separator } from '@/shared/components/ui/separator';
import { toast } from 'sonner';
import { boardApi } from '../services/eztaskApi';
import { useEzTask } from '../context/EzTaskContext';

const PRESET_COLORS = [
'#0079BF', // Blue
'#D29034', // Bronze
'#519839', // Green
'#B04632', // Red
'#89609E', // Purple
'#CD5A91', // Pink
'#4BBF6B', // Light Green
'#00AECC', // Sky
'#838C91' // Grey
];

export default function BoardSettings({ isOpen, onClose, board }) {const { t } = useTranslation("eztask");
  const { updateBoard, refreshBoard } = useEzTask();
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpdateBasic = async () => {
    if (!name.trim()) {
      toast.error('Board name is required');
      return;
    }
    setIsSaving(true);
    try {
      await updateBoard(board.board_id, { name, description });
      toast.success('Board settings updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update board');
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorSelect = async (color) => {
    try {
      await updateBoard(board.board_id, {
        background_color: color,
        background_image: null // Clear image when color selected
      });
      toast.success('Background color updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update color');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      const result = await boardApi.uploadBackground(board.board_id, file);
      // Update local state via refresh or context
      await refreshBoard();
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    }
  };

  const handleClearBackground = async () => {
    try {
      await updateBoard(board.board_id, {
        background_image: null,
        background_color: '#0079BF'
      });
      toast.success('Background cleared');
    } catch (err) {
      toast.error(err.message || 'Failed to clear background');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader className="pb-6 border-b">
          <SheetTitle className="flex items-center gap-2 text-2xl font-bold">
            <Settings className="h-6 w-6 text-primary" />{t("boardSettings", "Board Settings")}

          </SheetTitle>
          <SheetDescription>{t("customizeYourBoards", "Customize your board's appearance and details")}

          </SheetDescription>
        </SheetHeader>

        <div className="py-8 space-y-8">
          {/* Basic Info */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("generalInfo", "General Info")}</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("boardName", "Board Name")}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter board name"
                  className="bg-accent/10" />
                
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("description", "Description")}</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this board about?"
                  rows={4}
                  className="bg-accent/10 resize-none" />
                
              </div>
              <Button
                onClick={handleUpdateBasic}
                className="w-full"
                disabled={isSaving}>
                
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </section>

          <Separator />

          {/* Background Customization */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Palette className="h-4 w-4" />{t("appearance", "Appearance")}

              </h3>
              {(board.background_image || board.background_color !== '#0079BF') &&
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearBackground}
                className="text-red-500 hover:text-red-600 hover:bg-red-50">
                
                  <Trash2 className="h-4 w-4 mr-2" />{t("reset", "Reset")}

              </Button>
              }
            </div>

            <div className="space-y-6">
              {/* Preset Colors */}
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">{t("backgroundColor", "Background Color")}

                </label>
                <div className="grid grid-cols-5 gap-3">
                  {PRESET_COLORS.map((color) =>
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className="h-10 w-full rounded-md border-2 transition-all hover:scale-105 relative group flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: color, borderColor: board.background_color === color ? 'white' : 'transparent' }}>
                    
                      {board.background_color === color && !board.background_image &&
                    <Check className="h-5 w-5 text-white/90 drop-shadow-md" />
                    }
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-md" />
                    </button>
                  )}
                </div>
              </div>

              {/* Custom Image */}
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />{t("customBackground", "Custom Background")}

                </label>
                <div
                  className="relative group cursor-pointer border-2 border-dashed border-muted-foreground/30 rounded-xl overflow-hidden aspect-video flex flex-col items-center justify-center transition-all hover:bg-accent/10 hover:border-primary/50"
                  onClick={() => fileInputRef.current?.click()}>
                  
                  {board.background_image ?
                  <>
                      <img
                      src={(() => {
                        const path = board.background_image;
                        if (path.startsWith('http')) return path;
                        const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
                        return `${API_BASE}/${normalizedPath}`;
                      })()}
                      alt="Current Background"
                      className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110" />
                    
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex flex-col items-center text-white">
                          <Upload className="h-8 w-8 mb-2" />
                          <span className="text-sm font-bold uppercase">{t("changeImage", "Change Image")}</span>
                        </div>
                      </div>
                    </> :

                  <div className="flex flex-col items-center p-6 text-muted-foreground">
                      <Upload className="h-10 w-10 mb-4 opacity-40" />
                      <span className="text-sm font-medium text-center">{t("clickToUpload", "Click to upload or drag and drop")}</span>
                      <span className="text-xs opacity-60 mt-2">{t("svgPngJpg", "SVG, PNG, JPG (max. 5MB)")}</span>
                    </div>
                  }
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload} />
                  
                </div>
              </div>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>);

}